/* ═══════════════════════════════════════════════════════════════════════════════
   Projects Page Tour (v2.2.0)
   
   Feature update tour highlighting:
   - New "Upcoming" project status
   - Status-based visual differentiation (colored left borders)
   - Smart Claim Due logic (Active-only)
   - Variations column on project cards
   - Status filter dropdown with new option
   ═══════════════════════════════════════════════════════════════════════════════ */

import type { TourDefinition } from './types';

export const projectsFeatureTour: TourDefinition = {
  id: 'projects_features_v2_2',
  version: '2.2.0',
  type: 'feature_update',
  audience: 'all',
  page: 'projects',
  priority: 1,
  title: 'Projects Page Update',
  subtitle: 'New status types, smarter card indicators, and variations tracking at a glance.',

  steps: [
    // ── Step 0: Welcome ──────────────────────────────────────────────
    {
      id: 'welcome',
      type: 'welcome',
      title: 'What\'s New in Projects',
      description: 'We\'ve enhanced how you view and manage projects. Here\'s a quick look at what changed.',
      icon: '✨',
      actionLabel: 'Show Me',
    },

    // ── Step 1: Status Filter ────────────────────────────────────────
    {
      id: 'status-filter',
      type: 'spotlight',
      targetSelector: '[data-tour-id="projects-status-filter"]',
      position: 'bottom',
      title: 'New Status: Upcoming',
      description: 'Filter projects by the new "Upcoming" status. Plan ahead by creating projects before work begins, alongside Active, Completed, and On Hold.',
      icon: '🔮',
      actionLabel: 'Next',
      spotlightPadding: 8,
    },

    // ── Step 2: Card Visual Differentiation ──────────────────────────
    {
      id: 'card-border',
      type: 'spotlight',
      targetSelector: '[data-tour-id="projects-first-card"]',
      position: 'right',
      title: 'Status at a Glance',
      description: 'Each project card now has a colored left border that matches its status: green for Active, purple for Upcoming, blue for Completed, and amber for On Hold.',
      icon: '🎨',
      actionLabel: 'Next',
      spotlightPadding: 4,
    },

    // ── Step 3: Smart Claim Due ──────────────────────────────────────
    {
      id: 'claim-due',
      type: 'spotlight',
      targetSelector: '[data-tour-id="projects-claim-due"]',
      position: 'top',
      title: 'Smart Claim Due',
      description: 'The Claim Due indicator now only appears for Active projects. Completed, On Hold, and Upcoming projects show their status label instead, with no more noise.',
      icon: '🧠',
      actionLabel: 'Next',
      spotlightPadding: 8,
    },

    // ── Step 4: Variations Column ────────────────────────────────────
    {
      id: 'variations-column',
      type: 'spotlight',
      targetSelector: '[data-tour-id="projects-variations-col"]',
      position: 'top',
      title: 'Variations Tracking',
      description: 'See the total number of variations for each project right on the card. Click into any project to manage them in detail.',
      icon: '📋',
      actionLabel: 'Next',
      spotlightPadding: 8,
    },

    // ── Step 5: Completion ───────────────────────────────────────────
    {
      id: 'completion',
      type: 'completion',
      title: 'You\'re All Set!',
      description: 'Use the status filter to organise your pipeline and track variations across all your projects.',
      icon: '🎉',
      actionLabel: 'Start Exploring',
    },
  ],
};
